/** Display helpers for the operator dashboard. Amounts arrive from the API as
 * decimal euro strings; parsing to a number is for display only, never for
 * money calculations (those stay in integer micro-units server-side). */

export function formatEuros(value: string | number, minimumFractionDigits = 2): string {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) {
    return String(value);
  }
  return n.toLocaleString('fr-FR', { minimumFractionDigits, maximumFractionDigits: 6 });
}

/** Euros rounded to the cent, for summary views where micro-euro detail only adds noise. */
export function formatEurosCents(value: string | number): string {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) {
    return String(value);
  }
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Converts a `datetime-local` value (local time, no zone) to an ISO UTC string. */
export function datetimeLocalToIso(value: string): string | null {
  if (value.trim().length === 0) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toISOString();
}

/** Formats an ISO timestamp in the Europe/Paris business zone for display. */
export function formatEffectiveFrom(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleString('fr-FR', {
    timeZone: 'Europe/Paris',
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

/** Margin as a percentage of net revenue, clamped to [0, 100] for progress bars. */
export function marginPercent(net: string | number, margin: string | number): number {
  const netN = typeof net === 'number' ? net : Number(net);
  const marginN = typeof margin === 'number' ? margin : Number(margin);
  if (!Number.isFinite(netN) || netN <= 0 || !Number.isFinite(marginN)) {
    return 0;
  }
  return (marginN / netN) * 100;
}

/** Up-to-two-letter initials for an avatar, resilient to empty or single-word names. */
export function initials(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter((part) => part.length > 0);
  const first = parts[0];
  if (first === undefined) {
    return '—';
  }
  const second = parts[1];
  if (second === undefined) {
    return first.slice(0, 2).toUpperCase();
  }
  return `${first.charAt(0)}${second.charAt(0)}`.toUpperCase();
}

/** Non-breaking space between a figure and its unit (« 1 234,56 € », « 12,4 % »). */
export const NBSP = ' ';

/** Euro amount with micro-euro detail and its sign: « 1 234,56 € ». */
export function formatEur(value: string | number): string {
  return `${formatEuros(value)}${NBSP}€`;
}

/** Euro amount rounded to the cent and its sign, for indicators and totals: « 1 234,56 € ». */
export function formatEurCents(value: string | number): string {
  return `${formatEurosCents(value)}${NBSP}€`;
}

/** Percentage in French with one decimal: « 77,3 % ». */
export function formatPercent(value: number): string {
  return `${value.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}${NBSP}%`;
}

/** Current month as `YYYY-MM` (browser local time, the month selected by default). */
export function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

const MICRO_DIGITS = 6;
const DECIMAL_PATTERN = /^(-?)(\d+)(?:\.(\d+))?$/;

/**
 * Decimal euro string (« 0.001648 », « -1.5 ») to integer micro-euros; null when
 * the value is not a plain decimal. Totals shown on screen are summed this way so
 * the browser never adds floats.
 */
export function toMicro(value: string): bigint | null {
  const match = DECIMAL_PATTERN.exec(value.trim());
  if (match === null) {
    return null;
  }
  const [, sign = '', whole = '0', fraction = ''] = match;
  const padded = fraction.padEnd(MICRO_DIGITS, '0').slice(0, MICRO_DIGITS);
  const micro = BigInt(whole) * 1_000_000n + BigInt(padded);
  return sign === '-' ? -micro : micro;
}

/** Micro-euros back to a decimal euro string, trailing zeros removed (« 0.001236 »). */
export function microToDecimal(micro: bigint): string {
  const negative = micro < 0n;
  const absolute = negative ? -micro : micro;
  const whole = absolute / 1_000_000n;
  const fraction = (absolute % 1_000_000n)
    .toString()
    .padStart(MICRO_DIGITS, '0')
    .replace(/0+$/, '');
  return `${negative ? '-' : ''}${whole.toString()}${fraction.length > 0 ? `.${fraction}` : ''}`;
}

/** Exact sum of decimal euro strings, as a decimal string (unparsable values are skipped). */
export function sumEuros(values: readonly string[]): string {
  let total = 0n;
  for (const value of values) {
    total += toMicro(value) ?? 0n;
  }
  return microToDecimal(total);
}

/** True when a decimal euro string is strictly positive. */
export function isPositiveEuros(value: string): boolean {
  return (toMicro(value) ?? 0n) > 0n;
}
